export const AI_AUTOMATION_ATTEMPTED_STORAGE_KEY =
  "aiAutomationAttempted.vpn" as const;

/** localStorage key recording that capture spans the journey, not just the VPN step. */
const JOURNEY_SESSION_RECORDED_STORAGE_KEY =
  "aiJourneySessionRecorded" as const;

/** Records a marker, tolerating storage being unavailable: it only ever gates an optional link. */
const writeMarker = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Deliberately swallowed; see above.
  }
};

/**
 * Mark that the recorded session covers the whole journey, for the person it belongs to.
 *
 * Kept apart from {@link markAiAutomationAttempted}, which says only that a session was opened at
 * all. The person is stored because laptops are handed on.
 *
 * @param personId The hire the recording belongs to.
 */
export const markJourneySessionRecorded = (personId: string): void =>
  writeMarker(JOURNEY_SESSION_RECORDED_STORAGE_KEY, personId);

/**
 * Whether a session-wide recording exists for this hire.
 *
 * @param personId The hire asking, or undefined before one is resolved.
 */
export const hasRecordedJourneySession = (
  personId: string | undefined
): boolean => {
  if (personId === undefined) {
    return false;
  }
  try {
    return (
      localStorage.getItem(JOURNEY_SESSION_RECORDED_STORAGE_KEY) === personId
    );
  } catch {
    return false;
  }
};

/**
 * Mark that a session an IT Support Engineer could replay was actually opened.
 *
 * Set when a run begins, never when consent is given: it is what unlocks the review link in the chat
 * handoff, and a hire who agreed but never reached a recorded step has nothing to review.
 */
export const markAiAutomationAttempted = (): void =>
  writeMarker(AI_AUTOMATION_ATTEMPTED_STORAGE_KEY, "true");

/**
 * Rollout gate for session-wide capture: `d1-ezoJourneyCapture`, served with the prefix trimmed.
 * Named once so the surfaces that read it cannot drift onto different spellings.
 */
export const JOURNEY_CAPTURE_FLAG = "ezoJourneyCapture";

/**
 * Async describe cutover for the observe loop: `d1-observeAsyncSummarizer`, served with the prefix
 * trimmed. When on, the host is told to hand each frame to the async Screenshot Summarizer (upload
 * to quarantine + a summary-absent row) instead of describing inline (NHE-6373). Read with no
 * default: a default of `true` would move traffic to the async path before the flag is provisioned.
 */
export const OBSERVE_ASYNC_SUMMARIZER_FLAG = "observeAsyncSummarizer";

/**
 * Linked from the consent copy, which every AI consent surface has to cite.
 *
 * Defined once because the consent screen and the VPN prompt have to name the same notice, so
 * that changing which document we point a hire at stays a single deliberate edit.
 */
export const GLOBAL_EMPLOYEE_PRIVACY_NOTICE_URL =
  "https://inside.hr.amazon.dev/content/inside/us/en/employment/policies-global/employee-privacy-notice/global.html";
